import {
    Alert,
    Box,
    Button,
    Flex,
    Progress,
    Spinner,
    Stack,
    Text,
} from '@chakra-ui/react';
import BuffettRatingCard from '../../../components/BuffettRatingCard';
import RatingUsageIndicator from '../../../components/RatingUsageIndicator';

function StockAnalysisRatingPanel({
    normalizedTicker,
    authState,
    usageState,
    ratingState,
    loadingState,
    onGenerateRating,
}) {
    const { authenticated } = authState;
    const {
        data: usage,
        loading: usageLoading,
        error: usageError,
        label: usageLabel,
        usedRatings,
        monthlyLimit,
        premiumMember,
        renewalDate,
        canGenerate,
    } = usageState;
    const {
        result: ratingResult,
        loading: ratingLoading,
        error: ratingError,
        buttonDisabled: ratingButtonDisabled,
    } = ratingState;
    const { messageIndex, progress, messages } = loadingState;

    return (
        <Stack spacing={4} width="100%" mb={4}>
            {authenticated ? (
                <Box borderWidth="1px" borderRadius="lg" p={4} bg="white" boxShadow="sm">
                    <Flex justify="space-between" align={{ base: 'flex-start', md: 'center' }} direction={{ base: 'column', md: 'row' }} gap={3}>
                        <Box>
                            <Text fontSize="lg" fontWeight="bold">
                                AI Rating
                            </Text>
                            <br />
                            <Text fontSize="sm" color="gray.600">
                                Let our teacher-grade AI score {normalizedTicker || 'this stock'}.
                            </Text>
                        </Box>
                        <Button
                            colorScheme="teal"
                            onClick={onGenerateRating}
                            isLoading={ratingLoading}
                            isDisabled={ratingButtonDisabled || !authenticated}
                        >
                            {canGenerate ? 'Get AI Rating' : 'Limit Reached'}
                        </Button>
                    </Flex>
                    <Text fontSize="sm" color="gray.600" mt={2}>
                        {usageLabel}
                    </Text>
                </Box>
            ) : (
                <Box borderWidth="1px" borderRadius="lg" p={4} bg="white" boxShadow="sm">
                    <Text fontSize="lg" fontWeight="bold">
                        AI Rating
                    </Text>
                    <Text fontSize="sm" color="gray.600">
                        Log in to generate premium-style analysis for {normalizedTicker || 'this stock'}.
                    </Text>
                </Box>
            )}

            {authenticated && usageLoading && !usage && (
                <Box borderWidth="1px" borderRadius="lg" p={4} bg="white" boxShadow="sm">
                    <Flex align="center" gap={3}>
                        <Spinner size="sm" color="teal.500" />
                        <Text fontSize="sm" color="gray.600">Checking your usage…</Text>
                    </Flex>
                </Box>
            )}

            {authenticated && usage && (
                <RatingUsageIndicator
                    used={usedRatings}
                    limit={monthlyLimit}
                    canGenerate={canGenerate}
                    isPremium={premiumMember}
                    renewalDate={renewalDate}
                />
            )}

            {ratingLoading && (
                <Box borderWidth="1px" borderRadius="lg" p={6} bg="white" boxShadow="sm">
                    <Stack gap={4}>
                        <Flex align="center" gap={3}>
                            <Spinner size="md" color="teal.500" />
                            <Text fontSize="lg" fontWeight="semibold" color="teal.700">
                                Analyzing {normalizedTicker}...
                            </Text>
                        </Flex>
                        <Text fontSize="md" color="gray.600" minH="24px">
                            {messages[messageIndex]}
                        </Text>
                        <Progress.Root
                            value={Math.min(progress, 100)}
                            colorPalette="teal"
                            borderRadius="md"
                        >
                            <Progress.Track>
                                <Progress.Range />
                            </Progress.Track>
                        </Progress.Root>
                        <Text fontSize="xs" color="gray.500" textAlign="center">
                            This usually takes 15-30 seconds
                        </Text>
                    </Stack>
                </Box>
            )}

            {usageError && (
                <Alert.Root status="warning">
                    <Alert.Indicator />
                    <Alert.Content>{usageError}</Alert.Content>
                </Alert.Root>
            )}

            {ratingError && (
                <Alert.Root status="error">
                    <Alert.Indicator />
                    <Alert.Content>{ratingError}</Alert.Content>
                </Alert.Root>
            )}

            {authenticated && ratingResult && (
                <BuffettRatingCard rating={ratingResult} />
            )}
        </Stack>
    );
}

export default StockAnalysisRatingPanel;
